import { useEffect } from 'react';
import { css } from '@emotion/react';
import { Setting } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { getUser } from '@frontend/auth-helpers';
import {
  GlobalSearch,
  GlobalSearchModuleOrder,
  GlobalSearchTrackingIds,
  useGlobalSearch,
  useGlobalRecentSearches,
} from '@frontend/global-search';
import { useTranslation } from '@frontend/i18n';
import {
  formatResultsPrimaryKeys,
  handleRegisterClick,
  handleUpdateSearch,
  useSearchHistoryShallowStore,
} from '@frontend/search-history';
import { useFilterSettingsOld } from '@frontend/search-hooks';
import {
  StaticSettingsRoutes,
  useSettingsNavigate,
  useSettingsSearchAccessRoutePaths,
} from '@frontend/settings-routing';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { NakedButton, SkeletonLoader } from '@frontend/design-system';
import { SettingsSearchItem, SettingsSearchItemOld } from '../../layout/settings-nav/settings-search';

const defaultMaxResults = 4;
const entity = Entity.ENTITY_SETTING;

// TODO: remove this old module when semantic-search FF is fully implemented
export const SettingsGlobalSearchModuleOld = () => {
  const { t } = useTranslation('global-search');
  const { navigate } = useSettingsNavigate();

  const { debouncedSearchTerm, viewAllFeature, close } = useGlobalSearch([
    'debouncedSearchTerm',
    'viewAllFeature',
    'close',
  ]);

  const { addSearch } = useGlobalRecentSearches();

  const searchedRoutePathsPrefiltered = useFilterSettingsOld(debouncedSearchTerm);

  const { searchId } = useSearchHistoryShallowStore('searchId');

  useEffect(() => {
    handleUpdateSearch({
      entity,
      results: formatResultsPrimaryKeys<{
        labelPath: string;
        path: string;
      }>('path', searchedRoutePathsPrefiltered),
    });
  }, [searchId, searchedRoutePathsPrefiltered]);

  const searchedRoutePaths = useSettingsSearchAccessRoutePaths(searchedRoutePathsPrefiltered);

  const maxResults =
    viewAllFeature === 'settings' ? searchedRoutePaths : searchedRoutePaths.slice(0, defaultMaxResults);

  return (
    <GlobalSearch.Section.Content
      hasResults={!!searchedRoutePaths.length}
      showViewAll={searchedRoutePaths.length > defaultMaxResults}
      count={`${searchedRoutePaths.length}`}
    >
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
        `}
      >
        {maxResults.length ? (
          maxResults.map((settingsItem, index) => {
            return (
              <NakedButton
                key={settingsItem.path}
                data-trackingid={GlobalSearchTrackingIds.searchResultItem('settings')}
                onClick={() => {
                  addSearch(debouncedSearchTerm, 'settings');
                  navigate({ to: settingsItem.path.slice(0, -1) as StaticSettingsRoutes });
                  handleRegisterClick({
                    entity,
                    entityId: settingsItem.path,
                    entityPosition: GlobalSearchModuleOrder.settings,
                    itemPosition: index,
                  });
                  close();
                }}
                css={css`
                  display: block;
                  border: 1px solid ${theme.colors.neutral10};
                  border-radius: ${theme.borderRadius.small};
                  padding: ${theme.spacing(2)};
                  min-width: 234px;
                  flex: 1;
                  text-decoration: none;
                  transition: background-color 0.3s ease;
                  background-color: ${theme.colors.white};
                  color: initial;
                  :hover {
                    background-color: ${theme.colors.neutral5};
                  }
                  span {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                    display: inline;
                    text-align: start;
                  }
                `}
              >
                <SettingsSearchItemOld item={settingsItem} />
              </NakedButton>
            );
          })
        ) : (
          <GlobalSearch.EmptyState customMessage={t('No settings path matches this search.')} />
        )}
      </div>
    </GlobalSearch.Section.Content>
  );
};

type Props = {
  isLoading: boolean;
  searchedSettings: Setting[];
};

export const SettingsGlobalSearchModule = ({ isLoading, searchedSettings }: Props) => {
  const { t } = useTranslation('global-search');
  const { navigate } = useSettingsNavigate();
  const userID = getUser()?.userID || '';

  const { debouncedSearchTerm, viewAllFeature, close } = useGlobalSearch([
    'debouncedSearchTerm',
    'viewAllFeature',
    'close',
  ]);

  const { addSearch } = useGlobalRecentSearches();

  const maxResults = viewAllFeature === 'settings' ? searchedSettings : searchedSettings.slice(0, defaultMaxResults);

  if (isLoading) {
    return (
      <div css={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
        {Array.from({ length: defaultMaxResults }).map((_, index) => (
          <SettingsItemSkeletonLoader key={index} />
        ))}
      </div>
    );
  }

  return (
    <GlobalSearch.Section.Content
      hasResults={!!searchedSettings.length}
      showViewAll={searchedSettings.length > defaultMaxResults}
      count={`${searchedSettings.length}`}
    >
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
        `}
      >
        {maxResults.length ? (
          maxResults.map((settingsItem, index) => {
            return (
              <NakedButton
                key={settingsItem.path}
                data-trackingid={GlobalSearchTrackingIds.searchResultItem('settings')}
                onClick={() => {
                  addSearch(debouncedSearchTerm, 'settings');
                  navigate({ to: settingsItem.path as StaticSettingsRoutes });
                  handleRegisterClick({
                    entity,
                    entityId: settingsItem.id,
                    entityPosition: GlobalSearchModuleOrder.settings,
                    itemPosition: index,
                  });

                  // Track the click event with Pendo
                  pendo.track('global_semantic_search_query_click', {
                    clickedSettingId: settingsItem.id,
                    clickedSettingPath: settingsItem.path,
                    entity: [Entity.ENTITY_SETTING],
                    query: debouncedSearchTerm,
                    timestamp: new Date().toISOString(),
                    userId: userID,
                  });
                  close();
                }}
                css={css`
                  display: block;
                  border: 1px solid ${theme.colors.neutral10};
                  border-radius: ${theme.borderRadius.small};
                  padding: ${theme.spacing(2)};
                  min-width: 234px;
                  flex: 1;
                  text-decoration: none;
                  transition: background-color 0.3s ease;
                  background-color: ${theme.colors.white};
                  color: initial;
                  :hover {
                    background-color: ${theme.colors.neutral5};
                  }
                  span {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                    display: inline;
                    text-align: start;
                  }
                `}
              >
                <SettingsSearchItem {...settingsItem} />
              </NakedButton>
            );
          })
        ) : (
          <GlobalSearch.EmptyState customMessage={t('No settings path matches this search.')} />
        )}
      </div>
    </GlobalSearch.Section.Content>
  );
};

const SettingsItemSkeletonLoader = () => {
  return (
    <div
      css={{
        minWidth: 234,
        border: `1px solid ${theme.colors.neutral10}`,
        borderRadius: theme.borderRadius.small,
        padding: theme.spacing(2),
        flex: 1,
      }}
    >
      <SkeletonLoader css={{ marginBottom: theme.spacing(1) }} height={20} width='40%' />
      <SkeletonLoader width='100%' height={18} />
    </div>
  );
};
